import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Grid, List } from 'lucide-react';
import { colors, spacing, typography, borderRadius, breakpoints } from '../styles/theme';
import { useTranslations } from '../locales/useI18n';
import EventList from '../components/EventList';
import { EventFilters } from '../types/api';
import SEO, { createBreadcrumbStructuredData } from '../components/SEO';
import GenericSearchResults from '../components/events/GenericSearchResults';
import { localCalendarDateOffset } from '../utils/eventDateTime';
import { canonicalUrl } from '../utils/seo';
import {
  DEFAULT_UPCOMING_HORIZON_DAYS,
  UPCOMING_HORIZON_OPTIONS,
  type UpcomingHorizonDays,
} from '../utils/eventFilters';
import { useAnalytics } from '../hooks/useAnalytics';

const BrowsePage = styled.div`
  min-height: 100vh;
  background: ${colors.background.primary};
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: ${spacing[8]} ${spacing[4]};
  
  @media (min-width: ${breakpoints.sm}) {
    padding: ${spacing[8]} ${spacing[6]};
  }
  
  @media (min-width: ${breakpoints.lg}) {
    padding: ${spacing[8]} ${spacing[8]};
  }
`;

const PageHeader = styled.div`
  margin-bottom: ${spacing[8]};
  
  @media (min-width: ${breakpoints.md}) {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-end;
    gap: ${spacing[6]};
  }
`;

// `min-width: 0` plus a real flex-basis stops the control groups' intrinsic
// width from collapsing the title into a one-word-per-line column.
const PageHeaderContent = styled.div`
  flex: 1 1 320px;
  min-width: 0;
  margin-bottom: ${spacing[4]};
  
  @media (min-width: ${breakpoints.md}) {
    margin-bottom: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: ${typography.fontSizes['3xl']};
  font-weight: ${typography.fontWeights.bold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[4]};
  
  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes['2xl']};
  }
`;

const PageDescription = styled.p`
  font-size: ${typography.fontSizes.lg};
  color: ${colors.text.secondary};
  margin-bottom: ${spacing[6]};
  
  @media (max-width: ${breakpoints.md}) {
    font-size: ${typography.fontSizes.base};
  }
`;

const HeaderControls = styled.div`
  display: flex;
  gap: ${spacing[3]};
  align-items: center;
  flex-wrap: wrap;
  min-width: 0;
  
  @media (max-width: ${breakpoints.md}) {
    justify-content: center;
    gap: ${spacing[2]};
  }
`;

const UpcomingToggle = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-radius: ${borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${colors.border.normal};
`;

const ViewToggle = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-radius: ${borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${colors.border.normal};
`;

const ToggleButton = styled.button`
  padding: ${spacing[2]} ${spacing[4]};
  background: ${colors.white};
  border: none;
  border-right: 1px solid ${colors.border.normal};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.primary};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    background: ${colors.primary[50]};
    color: ${colors.primary[600]};
  }
  
  &.active {
    background: ${colors.primary[500]};
    color: ${colors.white};
  }
`;

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[4]};
  background: ${colors.white};
  border: none;
  border-right: 1px solid ${colors.border.normal};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.primary};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    background: ${colors.primary[50]};
    color: ${colors.primary[600]};
  }
  
  &.active {
    background: ${colors.primary[500]};
    color: ${colors.white};
  }
`;

/** Geographic search radius options, in km. 50 is the default. */
const RADIUS_OPTIONS_KM = [25, 50, 100, 250];
const DEFAULT_RADIUS_KM = 50;

export const BrowsePageComponent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // List is the default view everywhere; grid stays available as an option.
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(true);
  const [filters, setFilters] = useState<EventFilters>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const { t } = useTranslations();
  const { trackEvent } = useAnalytics();

  const isGeoSearch = searchParams.get('geoCity') === '1' && Boolean(searchParams.get('city'));
  const selectedRadiusKm = Number(searchParams.get('radiusKm')) || DEFAULT_RADIUS_KM;
  const parsedHorizonDays = Number(searchParams.get('days'));
  const horizonDays = UPCOMING_HORIZON_OPTIONS.includes(parsedHorizonDays as UpcomingHorizonDays)
    ? parsedHorizonDays as UpcomingHorizonDays
    : DEFAULT_UPCOMING_HORIZON_DAYS;

  const handleRadiusChange = (radiusKm: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('radiusKm', String(radiusKm));
    setSearchParams(next);
  };

  const handleHorizonChange = (days: UpcomingHorizonDays) => {
    const next = new URLSearchParams(searchParams);
    if (days === DEFAULT_UPCOMING_HORIZON_DAYS) next.delete('days');
    else next.set('days', String(days));
    next.delete('endDate');
    setSearchParams(next);
    trackEvent({
      category: 'browse_events',
      action: 'filter_change',
      value: days,
      custom_parameters: {
        filter_group: 'horizonDays',
        horizon_days: days,
      },
    });
  };

  useEffect(() => {
    // Parse URL parameters
    const urlCity = searchParams.get('city');
    const urlState = searchParams.get('state');
    const urlCountry = searchParams.get('country');
    const urlEventType = searchParams.get('eventType');
    const urlKeyword = searchParams.get('keyword'); // For entity name search
    const urlEntity = searchParams.get('entity'); // For entity search from suggestions
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');
    const urlTimeFilter = searchParams.get('timeFilter');
    const urlSearch = searchParams.get('search'); // New: for vector search
    const urlGeoCity = searchParams.get('geoCity'); // New: route through getEventsByCity (geo-radius search)
    const urlRadiusKm = searchParams.get('radiusKm');

    // Convert URL parameters to EventFilters format
    const newFilters: EventFilters = {};

    if (urlCity) newFilters.city = urlCity;
    if (urlState) newFilters.state = urlState;
    if (urlCountry) newFilters.country = urlCountry;
    if (urlEventType) newFilters.eventType = urlEventType;
    if (urlKeyword) newFilters.entityName = urlKeyword; // Map keyword to entityName for searching
    if (urlEntity) newFilters.entityName = urlEntity; // Map entity to entityName for searching
    if (urlStartDate) newFilters.startDateFrom = urlStartDate;
    if (urlEndDate) newFilters.endDateTo = urlEndDate;
    else newFilters.startDateTo = localCalendarDateOffset(horizonDays);
    newFilters.horizonDays = horizonDays;
    if (urlGeoCity && urlCity) newFilters.useGeoCity = true;
    const parsedRadius = Number(urlRadiusKm);
    newFilters.radiusKm = Number.isFinite(parsedRadius) && parsedRadius > 0 ? parsedRadius : DEFAULT_RADIUS_KM;
    
    // Add search query to filters for vector search
    if (urlSearch) {
      newFilters.searchQuery = urlSearch;
    }
    
    // Handle time filter conversions
    if (urlTimeFilter === 'upcoming') {
      setShowUpcomingOnly(true);
      newFilters.upcomingOnly = true;
    } else if (urlTimeFilter === 'all') {
      setShowUpcomingOnly(false);
    } else {
      // Default to upcoming if no time filter specified and we have location filters
      if (urlCity || urlState) {
        setShowUpcomingOnly(true);
        newFilters.upcomingOnly = true;
      } else {
        // For homepage with no location, default to upcoming
        setShowUpcomingOnly(true);
        newFilters.upcomingOnly = true;
      }
    }
    
    // Only update filters if they're actually different
    setFilters(prevFilters => {
      const hasChanged = JSON.stringify(prevFilters) !== JSON.stringify(newFilters);
      if (hasChanged) {
        setIsInitialized(true);
        return newFilters;
      } else {
        if (!isInitialized) setIsInitialized(true);
        return prevFilters;
      }
    });
  }, [searchParams, isInitialized, horizonDays]);

  const handleUpcomingToggle = (upcoming: boolean) => {
    setShowUpcomingOnly(upcoming);
    
    // Update URL with new filter
    const newParams = new URLSearchParams(searchParams);
    if (upcoming) {
      newParams.set('timeFilter', 'upcoming');
    } else {
      newParams.set('timeFilter', 'all');
    }
    setSearchParams(newParams);
  };

  const getPageTitle = () => {
    const city = searchParams.get('city');
    const state = searchParams.get('state');
    const type = searchParams.get('type');
    const entity = searchParams.get('entity');
    const eventType = searchParams.get('eventType');
    const search = searchParams.get('search');
    const geoCity = searchParams.get('geoCity');
    
    if (search) {
      return t('browse.searchResultsTitle').replace('{query}', search);
    } else if (eventType) {
      return `${eventType} Events`;
    } else if (geoCity && city) {
      return state ? `Events near ${city}, ${state}` : `Events near ${city}`;
    } else if (city && state) {
      return `Events in ${city}, ${state}`;
    } else if (city) {
      return `Events in ${city}`;
    } else if (entity) {
      return `${entity} Events`;
    } else if (type === 'popular' || type === 'trending') {
      return 'Popular Events';
    }
    
    return t('browse.title');
  };

  const getPageDescription = () => {
    const city = searchParams.get('city');
    const type = searchParams.get('type');
    const entity = searchParams.get('entity');
    const eventType = searchParams.get('eventType');
    const search = searchParams.get('search');
    const geoCity = searchParams.get('geoCity');
    
    if (search) {
      return t('browse.searchResultsDescription').replace('{query}', search);
    } else if (eventType) {
      return `Discover conferences, workshops, and events in the ${eventType} category`;
    } else if (geoCity && city) {
      return `Events within a ${selectedRadiusKm} km radius of ${city} — including events in nearby municipalities, ranked by date.`;
    } else if (city) {
      return `Discover conferences, workshops, and networking events happening in ${city}`;
    } else if (entity) {
      return `Explore events hosted and organized by ${entity}`;
    } else if (type === 'popular' || type === 'trending') {
      return 'Discover upcoming events connected to the most participating organizations';
    }
    
    return 'Explore thousands of events from conferences to workshops, webinars to networking meetups';
  };

  const searchQuery = searchParams.get('search');
  if (searchQuery) {
    return (
      <BrowsePage>
        <SEO
          title={`${getPageTitle()} | Event Finder`}
          description={getPageDescription()}
          keywords="event search, semantic event search, conferences, workshops, networking, event discovery"
          canonical={canonicalUrl(`/browse${window.location.search}`)}
          structuredData={[
            createBreadcrumbStructuredData([
              { name: 'Home', url: 'https://www.eventfinder.site/' },
              { name: 'Search Events', url: 'https://www.eventfinder.site/browse' },
            ]),
          ]}
        />
        <GenericSearchResults
          query={searchQuery}
          title={getPageTitle()}
          description={getPageDescription()}
        />
      </BrowsePage>
    );
  }

  return (
    <BrowsePage>
      <SEO
        title={`${getPageTitle()} | Event Finder`}
        description={getPageDescription()}
        keywords="browse events, find events, conferences, workshops, networking, event search, event discovery"
        canonical={canonicalUrl(`/browse${window.location.search}`)}
        structuredData={[
          createBreadcrumbStructuredData([
            { name: 'Home', url: 'https://www.eventfinder.site/' },
            { name: 'Browse Events', url: 'https://www.eventfinder.site/browse' }
          ])
        ]}
      />
      <Container>
        <PageHeader>
          <PageHeaderContent>
            <PageTitle>{getPageTitle()}</PageTitle>
            <PageDescription>{getPageDescription()}</PageDescription>
          </PageHeaderContent>
          
          <HeaderControls>
            <UpcomingToggle role="group" aria-label="Upcoming event range">
              {UPCOMING_HORIZON_OPTIONS.map((days) => (
                <ToggleButton
                  key={days}
                  className={!searchParams.get('endDate') && horizonDays === days ? 'active' : ''}
                  onClick={() => handleHorizonChange(days)}
                  aria-pressed={!searchParams.get('endDate') && horizonDays === days}
                >
                  {`Next ${days} days`}
                </ToggleButton>
              ))}
            </UpcomingToggle>

            {isGeoSearch && (
              <UpcomingToggle role="group" aria-label="Search radius">
                {RADIUS_OPTIONS_KM.map((radiusKm) => (
                  <ToggleButton
                    key={radiusKm}
                    className={selectedRadiusKm === radiusKm ? 'active' : ''}
                    onClick={() => handleRadiusChange(radiusKm)}
                    aria-pressed={selectedRadiusKm === radiusKm}
                  >
                    {`${radiusKm} km`}
                  </ToggleButton>
                ))}
              </UpcomingToggle>
            )}

            <UpcomingToggle role="group" aria-label="Event time filter">
              <ToggleButton
                className={showUpcomingOnly ? 'active' : ''}
                onClick={() => handleUpcomingToggle(true)}
                aria-pressed={showUpcomingOnly}
              >
                Upcoming Events
              </ToggleButton>
              <ToggleButton
                className={!showUpcomingOnly ? 'active' : ''}
                onClick={() => handleUpcomingToggle(false)}
                aria-pressed={!showUpcomingOnly}
              >
                All Events
              </ToggleButton>
            </UpcomingToggle>
            
            <ViewToggle role="group" aria-label="View mode">
              <ViewButton
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
                aria-pressed={viewMode === 'list'}
                aria-label="List view"
              >
                <List size={16} />
                List View
              </ViewButton>
              <ViewButton
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                aria-pressed={viewMode === 'grid'}
                aria-label="Grid view"
              >
                <Grid size={16} />
                Grid View
              </ViewButton>
            </ViewToggle>
          </HeaderControls>
        </PageHeader>

        {isInitialized && (
          <EventList
            filters={filters}
            title=""
            showFilters={false}
            pageSize={20}
            viewMode={viewMode}
          />
        )}
      </Container>
    </BrowsePage>
  );
};

export default BrowsePageComponent;
